import './ProfilePage.scss';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import BackLink from '@components/BackLink';
import Badge from '@components/Badge';
import { BadgeStyle } from '@components/Badge/Badge';
import Button, { ButtonStyle } from '@components/Button';
import Layout from '@components/Layout';
import { Tab } from '@components/Tabs';
import Tabs from '@components/Tabs/Tabs';
import { RoutePath } from '@constants/route-path';
import { useInjection } from '@src/components/Injection';
import { useAuth } from '@src/contexts/authContext';
import { auth } from '@src/firebase/firebase';
import { CLIENT_SERVICE, ClientService } from '@src/services/client.service';
import { doEmailChange, doPasswordChange, doReauthenticateUser } from '../firebase/auth';

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^\d{10}$/;
  return phoneRegex.test(phone);
};

const validatePassword = (password: string): boolean => {
  const minLength = 8;
  const conditions = [
    (pw) => pw.length >= minLength,
    (pw) => /[A-Z]/.test(pw),
    (pw) => /[a-z]/.test(pw),
    (pw) => /[0-9]/.test(pw),
    (pw) => /[-!@#$%^&*(),.?":{}|<>]/.test(pw),
  ];

  return conditions.every((condition) => condition(password));
};

function ProfilePage() {
  const navigate = useNavigate();

  const authContext = useAuth();
  const clientService = useInjection<ClientService>(CLIENT_SERVICE);

  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [email, setEmail] = useState(auth.currentUser.email);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState(authContext.userObject.phone);
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [isButtonDisabled, setIsButtonDisabled] = useState([true, true]);
  const [password, setPassword] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState(['', '']);
  const [isNewPasswordValid, setIsNewPasswordValid] = useState(true);
  const [doPasswordsMatch, setDoPasswordsMatch] = useState(true);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [passError, setPassError] = useState<string | null>(null);
  const [passSuccess, setPassSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState([false, false, false, false]);
  console.log(authContext.userObject);

  useEffect(() => {
    if (inputRef.current) {
      const pos = phoneNumber.length;
      inputRef.current.setSelectionRange(pos, pos);
    }
  }, [phoneNumber]);

  useEffect(() => {
    if (
      (isEmailValid && email !== auth.currentUser.email) ||
      (isPhoneValid && phoneNumber.replace(/-/g, '') !== authContext.userObject.phone.replace(/-/g, ''))
    ) {
      setIsButtonDisabled((prevButton) => {
        const updatedButton = [...prevButton];
        updatedButton[0] = false;
        return updatedButton;
      });
    } else {
      setIsButtonDisabled((prevButton) => {
        const updatedButton = [...prevButton];
        updatedButton[0] = true;
        return updatedButton;
      });
    }
    if (isNewPasswordValid && doPasswordsMatch && oldPassword && newPassword[0] && newPassword[1]) {
      setIsButtonDisabled((prevButton) => {
        const updatedButton = [...prevButton];
        updatedButton[1] = false;
        return updatedButton;
      });
    } else {
      setIsButtonDisabled((prevButton) => {
        const updatedButton = [...prevButton];
        updatedButton[1] = true;
        return updatedButton;
      });
    }
  }, [isEmailValid, isPhoneValid, email, auth, isNewPasswordValid, doPasswordsMatch, oldPassword]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    setEmail(emailValue);
    setIsEmailValid(validateEmail(emailValue));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleOldPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOldPassword(e.target.value);
  };

  const handleNewPasswordChange = (index, e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword((prevState) => {
      const updatedState = [...prevState];
      updatedState[index] = e.target.value;
      return updatedState;
    });
    setDoPasswordsMatch(newPassword[1 - index] === e.target.value);
    if (index === 0) {
      setIsNewPasswordValid(validatePassword(e.target.value));
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phoneValue = e.target.value.replace(/[^\d]/g, '');

    if (phoneValue.length > 10) {
      e.preventDefault();
    } else {
      setPhoneNumber(phoneValue);
      setIsPhoneValid(validatePhone(phoneValue));
    }
  };

  const formatPhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value;
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (cleaned === '') {
      setPhoneNumber(cleaned);
    } else if (match) {
      setPhoneNumber(`${match[1]}-${match[2]}-${match[3]}`);
    }
  };

  const formatPhoneFocus = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value;
    const cleaned = phone.replace(/\D/g, '');
    setPhoneNumber(cleaned);
  };

  function togglePasswordVisibility(index) {
    setShowPassword((prevVisibility) => {
      const updatedVisibility = [...prevVisibility];
      updatedVisibility[index] = !prevVisibility[index];
      return updatedVisibility;
    });
  }

  const handlePhoneKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/[\d]/.test(e.key) && e.key !== 'Backspace') {
      e.preventDefault();
    }
  };

  const handleInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email !== auth.currentUser.email) {
      try {
        await doReauthenticateUser(password);
        await doEmailChange(email);
        await clientService.updateEmail({ email: email }, auth.currentUser.uid);
        setInfoError(null);
        navigate(RoutePath.LoginPage);
      } catch (err) {
        console.error(err);
        setInfoError('Failed to re-authenticate you. Check your credentials.');
      }
    }
    if (phoneNumber !== authContext.userObject.phone) {
      try {
        await clientService.updatePhone({ phone: phoneNumber }, auth.currentUser.uid);
      } catch (err) {
        console.error(err);
        setInfoError('Failed to update phone number.');
      }
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await doReauthenticateUser(oldPassword);
      await doPasswordChange(newPassword[1]);
      setPassError(null);
      setPassSuccess('Your password has been updated successfully.');
      setOldPassword('');
      setNewPassword(['', '']);
    } catch (err) {
      console.log(err);
      setPassError('Failed to modify your password. Make sure your current password is correct.');
    }
  };

  return (
    <Layout>
      <BackLink to={RoutePath.PatientListPage}>{t('patientList.alternativeTitle')}</BackLink>
      <div className="modProfilePage">
        <div className="modProfilePage__avatar">
          {`${authContext.userObject.firstName?.charAt(0) ?? ''}${
            authContext.userObject.lastName?.charAt(0) ?? ''
          }`}
        </div>
        <div className="modProfilePage__name--title">
          <div className="modProfilePage__name">
            {authContext.userObject.firstName + ' ' + authContext.userObject.lastName}
          </div>
          <div className="modProfilePage__title">{authContext.userObject.role}</div>
        </div>
        {authContext.userObject.status && authContext.userObject.status.trim().length > 0 && (
          <div className="modProfilePage__status">
            <Badge style={BadgeStyle.Purple}>{authContext.userObject.status}</Badge>
          </div>
        )}
      </div>
      <Tabs>
        <Tab name="Notifications">
          <div className="modProfileInfo__subtitle">🎉 You’re all caught up!</div>
        </Tab>
        <Tab name="My information">
          <form onSubmit={handleInfoSubmit}>
            <div className="modProfileInfo__header">
              <div className="modProfilePage__title--subtitle">
                <div className="modProfileInfo__title">My information</div>
                <div className="modProfileInfo__subtitle">View and manage your information</div>
              </div>
              <div className="modProfileInfo__save">
                <Button type="submit" disabled={isButtonDisabled[0]} style={ButtonStyle.Primary}>
                  Save
                </Button>
              </div>
            </div>
            <div className="modProfileInfo__inputName">Email:</div>
            <input
              className={`modProfilePageForm__input ${!isEmailValid ? 'invalid' : ''}`}
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="example@modulate.ca"
            />
            {!isEmailValid && <p style={{ color: 'red' }}>Please enter a valid email address.</p>}
            {email !== auth.currentUser.email && (
              <>
                <div className="modProfileInfo__inputName">Password:</div>
                <input
                  className="modProfilePageForm__input"
                  type={showPassword[0] ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                />
                <button
                  type="button"
                  className="modProfileInfo__passwordToggle"
                  onClick={() => togglePasswordVisibility(0)}>
                  {showPassword[0] ? 'Hide' : 'Show'}
                </button>
              </>
            )}
            {infoError && <p style={{ color: 'red' }}>{infoError}</p>}
            <div className="modProfileInfo__inputName">Phone:</div>
            <input
              className="modProfilePageForm__input"
              type="tel"
              value={phoneNumber}
              onChange={handlePhoneChange}
              onKeyDown={handlePhoneKeydown}
              onBlur={formatPhoneNumber}
              onFocus={formatPhoneFocus}
              placeholder="(___) ___-____"
            />
            {!isPhoneValid && <p style={{ color: 'red' }}>Invalid phone number format</p>}
          </form>
        </Tab>
        <Tab name="Security">
          <form onSubmit={handlePasswordSubmit}>
            <div className="modProfileInfo__header">
              <div className="modProfilePage__title--subtitle">
                <div className="modProfileInfo__title">Security</div>
                <div className="modProfileInfo__subtitle">Manage your password</div>
              </div>
              <div className="modProfileInfo__save">
                <Button type="submit" disabled={isButtonDisabled[1]} style={ButtonStyle.Primary}>
                  Save
                </Button>
              </div>
            </div>
            <div className="modProfileInfo__inputName">Current password:</div>
            <input
              className="modProfilePageForm__input"
              type={showPassword[1] ? 'text' : 'password'}
              value={oldPassword}
              onChange={handleOldPasswordChange}
            />
            <button
              type="button"
              className="modProfileInfo__passwordToggle"
              onClick={() => togglePasswordVisibility(1)}>
              {showPassword[1] ? 'Hide' : 'Show'}
            </button>
            {passError && <p style={{ color: 'red' }}>{passError}</p>}
            {passSuccess && <p style={{ color: 'green' }}>{passSuccess}</p>}
            <div className="modProfileInfo__inputName">New password:</div>
            <input
              className="modProfilePageForm__input"
              type={showPassword[2] ? 'text' : 'password'}
              value={newPassword[0]}
              onChange={(e) => handleNewPasswordChange(0, e)}
            />
            <button
              type="button"
              className="modProfileInfo__passwordToggle"
              onClick={() => togglePasswordVisibility(2)}>
              {showPassword[2] ? 'Hide' : 'Show'}
            </button>
            {!isNewPasswordValid && (
              <p style={{ color: 'red' }}>Password must contain 8+ chars, upper, lower, num & special.</p>
            )}
            <div className="modProfileInfo__inputName">Confirm new password:</div>
            <input
              className="modProfilePageForm__input"
              type={showPassword[3] ? 'text' : 'password'}
              value={newPassword[1]}
              onChange={(e) => handleNewPasswordChange(1, e)}
            />
            <button
              type="button"
              className="modProfileInfo__passwordToggle"
              onClick={() => togglePasswordVisibility(3)}>
              {showPassword[3] ? 'Hide' : 'Show'}
            </button>
            {!doPasswordsMatch && <p style={{ color: 'red' }}>Passwords do not match.</p>}
          </form>
        </Tab>
      </Tabs>
    </Layout>
  );
}

export default ProfilePage;
